import { Dispatch } from 'react';

import { ISelectOption } from 'common/components/MulltipleSelectCheckmarks/MultipleSelectCheckmarks';
import {
	PRC_TABLE_TYPE,
	TAXONOMY_CURRENT_TAB_LIST,
	TAXONOMY_ENTITY_INDEX,
	TAXONOMY_TAB_VALUES,
} from 'common/constants/TaxonomyConstants';
import { setRiskRefSelectedTab } from 'common/store/reducer/commonReducer';
import {
	capitalizeName,
	formatNumber,
	toSentenceCase,
} from 'common/utils/utilFunctions';

import { handleSpecialCharacters } from '../TaxonomyUtils';

import {
	IApplyFilterParam,
	IDataBaseOptionsResponse,
	TaxonomySearchCount,
} from './api/type-def';
import {
	EXPORT_COLUMN_OPTIONS_CONTROLS,
	EXPORT_COLUMN_OPTIONS_PROCESSES,
	EXPORT_COLUMN_OPTIONS_RISKS,
	EXPORT_HEAT_MAP_BASED_ON_TYPE,
	EXPORT_TAB_LIST,
	EXPORT_COMBINED_COL_MAPPING,
} from './ExportColOptionConfig';

const { RISK_VALUE, PROCESS_VALUE, CONTROL_VALUE, POLICY_VALUE } =
	TAXONOMY_TAB_VALUES;

const { REG_INV_GRP, PROCESS, RISK, CONTROL, POLICY } = TAXONOMY_ENTITY_INDEX;

const { REACT_APP_ENVIRONMENT } = process.env;

/**
 * @method to get the exported count
 * @returns count
 */
export const getExportedCount = (
	exportType: string,
	getSearchCountTaxonomy: any,
) => {
	switch (exportType?.toLowerCase()) {
		case RISK_VALUE.toLowerCase():
			return getSearchCountTaxonomy?.riskCount || 0;
		case PROCESS_VALUE.toLowerCase():
			return getSearchCountTaxonomy?.processCount || 0;
		case CONTROL_VALUE.toLowerCase():
			return getSearchCountTaxonomy?.controlCount || 0;
		case POLICY_VALUE.toLowerCase():
			return getSearchCountTaxonomy?.policyCount || 0;
		default:
			return 0;
	}
};

/**
 * @method to get the source item
 * @returns array
 */
export const getSourceItem = (
	riskRefTaxonomySourceFiltersKey: string | undefined,
) => {
	if (riskRefTaxonomySourceFiltersKey) {
		return [
			{
				key: 'source',
				values: [riskRefTaxonomySourceFiltersKey.toUpperCase()],
			},
		];
	}
	return [];
};

export const generateExportPayloadForRiskReferenceTaxonomy = (
	options: ISelectOption[],
	source: string | undefined,
	exportTypeName: string | undefined,
	refSearchText: string,
	refFilterData: any,
	exportedCount: number,
) => {
	const checkedOptions = options.filter(
		(option: ISelectOption) => option.checked,
	);
	let colKeys: string[] = [];
	const lastOrderColKeys = ['modifiedDate', 'industryType', 'products'];
	const exportCols = checkedOptions
		.filter((option: ISelectOption) => !option.combined)
		.map((option: ISelectOption) => option.value);
	colKeys = colKeys.concat(
		exportCols.filter((value: string) => lastOrderColKeys.indexOf(value) < 0),
	);
	const combinedCols = checkedOptions
		.filter((option: ISelectOption) => !!option.combined)
		.map((option: ISelectOption) => {
			colKeys = colKeys.concat(option.combinedOptions || []);
			return {
				name: EXPORT_COMBINED_COL_MAPPING[option.value],
				columns: option.combinedOptions || [],
			};
		});
	if (exportCols.indexOf('modifiedDate') > -1) {
		colKeys.push('modifiedDate');
	}
	if (exportCols.indexOf('industryType') > -1) {
		colKeys.push('industryType');
	}
	if (exportCols.indexOf('products') > -1) {
		colKeys.push('products');
	}
	const sourceItem = getSourceItem(source);
	return {
		selectedColKeys: colKeys,
		payload: {
			indexName: `${exportTypeName}-index-${REACT_APP_ENVIRONMENT || 'dev'}`,
			searchText: handleSpecialCharacters(refSearchText),
			filters: sourceItem.concat(refFilterData),
			totalRecordsCount: exportedCount,
			export: {
				name: capitalizeName(exportTypeName || ''),
				columns: exportCols,
			},
			mappedTo: combinedCols || [],
		},
	};
};

/**
 * @method to generate payload for heatmap
 */
export const generatePayloadForHeatMapExport = (
	riskRefTaxonomySourceFiltersKey: string,
	riskRefTabIndex: number,
	env: string | undefined,
	refSearchText: string,
	refFilterData: any,
	getSearchCountTaxonomy: any,
) => {
	const sourceItem = getSourceItem(riskRefTaxonomySourceFiltersKey);

	const commonExportParams = {
		indexName: `${EXPORT_TAB_LIST[riskRefTabIndex].toLowerCase()}-index-${
			env || 'dev'
		}`,
		searchText: handleSpecialCharacters(refSearchText),
		exportType: 'Heatmap',
		filters: sourceItem.concat(refFilterData),
		totalRecordsCount: getExportedCount(
			EXPORT_TAB_LIST[riskRefTabIndex],
			getSearchCountTaxonomy,
		),
	};
	const getTabName = EXPORT_TAB_LIST[riskRefTabIndex];
	const getIndividualColumns = EXPORT_HEAT_MAP_BASED_ON_TYPE[getTabName];
	const getSpecificExportColumns = {
		...commonExportParams,
		...getIndividualColumns,
	};
	return getSpecificExportColumns;
};

/**
 * @method to set the tab index data
 */
export const setTabIndexData = (
	indexType: number,
	env: string | undefined,
	updateTab: Dispatch<any>,
) => {
	let tableIndex = '';

	switch (indexType) {
		case 0:
			tableIndex = `${PROCESS}-${env}`;
			break;
		case 1:
			tableIndex = `${RISK}-${env}`;
			break;
		case 2:
			tableIndex = `${CONTROL}-${env}`;
			break;
		case 3:
			tableIndex = `${REG_INV_GRP}-${env}`;
			break;
		case 4:
			tableIndex = `${POLICY}-${env}`;
			break;
		default:
			break;
	}
	updateTab(setRiskRefSelectedTab({ tableIndex, indexType }));
};

/**
 * @method to set the filter data
 */
export const filterArrayHandler = (data: any, resultArr: any) => {
	data.forEach((ele: any) => {
		const tempArr: IApplyFilterParam = {
			key: '',
			values: [],
		};
		if (resultArr.length > 0) {
			const indexData = resultArr.findIndex((obj: any) => obj.key === ele.type);
			if (indexData !== -1) {
				const val = resultArr[indexData].values;
				val.push(ele.label);
				resultArr[indexData].values = val;
			} else {
				tempArr.key = ele.type;
				tempArr.values.push(ele.label);
				resultArr.push(tempArr);
			}
		} else {
			tempArr.key = ele.type;
			tempArr.values.push(ele.label);
			resultArr.push(tempArr);
		}
	});
};

/**
 * @method to generate export options
 * @returns options
 */
export const generateExportOptions = (options: any) =>
	options.map((option: any) => ({
		checked: false,
		...option,
	}));

/**
 * @method to get export column options based on condition
 */
export const getExportColOptions = (exportType: string) => {
	switch (exportType) {
		case RISK_VALUE:
			return generateExportOptions(EXPORT_COLUMN_OPTIONS_RISKS);
		case PROCESS_VALUE:
			return generateExportOptions(EXPORT_COLUMN_OPTIONS_PROCESSES);
		case CONTROL_VALUE:
			return generateExportOptions(EXPORT_COLUMN_OPTIONS_CONTROLS);
		default:
			return [];
	}
};

export const getCategoryOptionList = (
	PRCEntityCount: TaxonomySearchCount,
): IDataBaseOptionsResponse[] =>
	TAXONOMY_CURRENT_TAB_LIST.map((tabValue, index) => {
		const count = formatNumber(
			PRCEntityCount?.[`${tabValue.toLowerCase()}Count`] ?? 0,
		);
		const label = `${toSentenceCase(PRC_TABLE_TYPE[index])} (${count})`;
		return {
			label,
			value: tabValue,
			id: tabValue,
			active: true,
		};
	});
